import React, { useState, useEffect, useMemo, useRef } from "react";

// SkillQuest - Mobile-first React Mini-game Prototype
// Purpose: Gamified micro-learning for SheEarns (individual empowerment)
// Features:
// - Quests made of short learning tasks (quiz/video/practice)
// - XP and Level system (Skill Gems)
// - Badge unlocks
// - LocalStorage persistence
// - Simple progress visualization
// Refactored: Mobile-first responsive design with Dark Pink theme
// Additions: Progress bar for quest tasks, Web Audio API for feedback sounds, enhanced hover animations
// Further: XP progress bar towards next level, more quests (now 5), smoother animations with longer durations and cubic-bezier easing
// Latest: More fluid easing curves using refined cubic-bezier values for natural, bouncy fluid motion

const STORAGE_KEY = "skillquest_progress_v1";

const DUMMY_QUESTS = [
  {
    id: 1,
    title: "Digital Basics",
    desc: "Learn the essentials of using mobile and internet safely.",
    tasks: [
      { id: 1, question: "What is a strong password?", options: ["12345", "Your name", "8+ chars mix of symbols & numbers"], answer: 2 },
      { id: 2, question: "Which app is used for online meetings?", options: ["YouTube", "Zoom", "Spotify"], answer: 1 },
    ],
  },
  {
    id: 2,
    title: "Financial Literacy",
    desc: "Understand saving, budgeting and digital transactions.",
    tasks: [
      { id: 1, question: "Which of these helps track expenses?", options: ["Budget App", "Music Player", "Camera"], answer: 0 },
      { id: 2, question: "UPI stands for?", options: ["Unified Payments Interface", "Universal Pay Info", "User Payment ID"], answer: 0 },
    ],
  },
  {
    id: 3,
    title: "Career Building",
    desc: "Build skills for job hunting and professional growth.",
    tasks: [
      { id: 1, question: "What is a resume?", options: ["A cover letter", "A summary of your skills and experience", "A reference list"], answer: 1 },
      { id: 2, question: "Best way to network?", options: ["Avoid social events", "Connect on LinkedIn", "Ignore emails"], answer: 1 },
    ],
  },
  {
    id: 4,
    title: "Health & Wellness",
    desc: "Prioritize mental and physical health in daily life.",
    tasks: [
      { id: 1, question: "How many hours of sleep recommended?", options: ["4-5", "7-9", "12+"], answer: 1 },
      { id: 2, question: "Mindfulness helps with?", options: ["Stress reduction", "Time travel", "Super speed"], answer: 0 },
    ],
  },
  {
    id: 5,
    title: "Entrepreneurship",
    desc: "Ideas to start and grow your own small business.",
    tasks: [
      { id: 1, question: "What is a business plan?", options: ["A shopping list", "A roadmap for your venture", "A recipe"], answer: 1 },
      { id: 2, question: "Market research involves?", options: ["Guessing", "Studying customer needs", "Ignoring competitors"], answer: 1 },
    ],
  },
];

export default function SkillQuest() {
  const [progress, setProgress] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [currentQuest, setCurrentQuest] = useState(DUMMY_QUESTS[0]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState("");
  const audioContextRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [progress]);

  const currentTask = currentQuest.tasks[currentTaskIndex];

  const playSound = (frequency, duration = 200, type = 'sine') => {
    if (!audioContextRef.current) return;
    const oscillator = audioContextRef.current.createOscillator();
    const gainNode = audioContextRef.current.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContextRef.current.destination);
    oscillator.frequency.value = frequency;
    oscillator.type = type;
    gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + duration / 1000);
    oscillator.start(audioContextRef.current.currentTime);
    oscillator.stop(audioContextRef.current.currentTime + duration / 1000);
  };

  const playCelebration = () => {
    playSound(600, 150);
    setTimeout(() => playSound(800, 150), 150);
    setTimeout(() => playSound(1000, 300), 300);
  };

  const handleAnswer = (idx) => {
    setSelected(idx);
    if (idx === currentTask.answer) {
      setFeedback("✅ Correct!");
      playSound(800, 300);
      const p = { ...progress };
      if (!p[currentQuest.id]) p[currentQuest.id] = { completed: 0, xp: 0 };
      p[currentQuest.id].completed++;
      p[currentQuest.id].xp += 10;
      setProgress(p);
      setTimeout(() => {
        if (currentTaskIndex < currentQuest.tasks.length - 1) {
          setCurrentTaskIndex(currentTaskIndex + 1);
        } else {
          setFeedback("🎉 Quest Completed!");
          playCelebration();
        }
        setSelected(null);
        setFeedback("");
      }, 1000);
    } else {
      setFeedback("❌ Try again.");
      playSound(400, 200, 'square');
      setTimeout(() => setSelected(null), 1000);
    }
  };

  const totalXP = useMemo(() => Object.values(progress).reduce((sum, q) => sum + q.xp, 0), [progress]);
  const level = 1 + Math.floor(totalXP / 50);
  const xpForNextLevel = level * 50;
  const progressToNextLevel = ((totalXP % 50) / 50) * 100;

  const resetGame = () => {
    setProgress({});
    setCurrentQuest(DUMMY_QUESTS[0]);
    setCurrentTaskIndex(0);
    setFeedback("");
  };

  const questProgressPercentage = ((currentTaskIndex + 1) / currentQuest.tasks.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900 p-4 sm:p-6 lg:p-8 text-gray-100 overflow-hidden">
      <style jsx>{`
        @keyframes slideIn { 
          from { opacity: 0; transform: translateY(20px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        .slide-in { 
          animation: slideIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; 
        }
        @keyframes glow { 
          0%, 100% { box-shadow: 0 0 5px rgba(244, 63, 94, 0.5); } 
          50% { box-shadow: 0 0 20px rgba(244, 63, 94, 0.8); } 
        }
        .glow { 
          animation: glow 2s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite; 
        }
        .smooth-transition { 
          transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1); 
        }
        .fluid-hover { 
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); 
        }
        .progress-fill { 
          transition: width 1.2s cubic-bezier(0.23, 1, 0.32, 1); 
        }
      `}</style>
      <main className="max-w-md mx-auto sm:max-w-lg md:max-w-xl lg:max-w-2xl w-full">
        <header className="mb-6 text-center slide-in">
          <h1 className="text-lg sm:text-2xl font-bold leading-tight mb-2 glow">💎 SkillQuest</h1>
          <p className="text-sm sm:text-base text-rose-300">Learn. Earn XP. Level up your confidence!</p>
        </header>

        <section className="bg-gray-800/50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-xl border border-rose-800/30 mb-4 sm:mb-6 smooth-transition">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center text-sm sm:text-base mb-4 gap-2">
            <div className="text-rose-300">Level <span className="font-bold text-white">{level}</span></div>
            <div className="text-rose-300">Total XP <span className="font-bold text-white">{totalXP}</span></div>
          </div>
          <div className="w-full bg-rose-800/30 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-rose-400 to-pink-400 h-2 rounded-full progress-fill shadow-sm glow"
              style={{ width: `${progressToNextLevel}%` }}
            ></div>
          </div>
          <p className="text-xs text-rose-400 mt-1">{Math.floor(totalXP % 50)} / 50 XP to Level {level + 1}</p>
        </section>

        <section className="bg-gray-800/50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-xl border border-rose-800/30 mb-6 sm:mb-8 smooth-transition">
          <h2 className="font-bold text-xl sm:text-2xl mb-2 text-white slide-in">{currentQuest.title}</h2>
          <p className="text-sm sm:text-base text-rose-300 mb-4 sm:mb-6">{currentQuest.desc}</p>

          <div className="text-sm sm:text-base">
            <div className="mb-3 font-semibold text-rose-200 slide-in">Question {currentTaskIndex + 1}/{currentQuest.tasks.length}</div>
            <div className="w-full bg-rose-800/30 rounded-full h-2 mb-4 overflow-hidden smooth-transition">
              <div 
                className="bg-gradient-to-r from-rose-400 to-pink-400 h-2 rounded-full progress-fill shadow-sm"
                style={{ width: `${questProgressPercentage}%` }}
              ></div>
            </div>
            <div className="mb-4 sm:mb-6 font-bold text-white slide-in">{currentTask.question}</div>

            <div className="space-y-2 sm:space-y-3">
              {currentTask.options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  disabled={!!selected}
                  className={`w-full text-left p-3 sm:p-4 rounded-xl smooth-transition text-sm sm:text-base border-2 border-rose-700/50 group transform-gpu fluid-hover ${
                    selected === null
                      ? 'bg-gray-700/30 hover:bg-rose-800/20 hover:border-rose-600/50 hover:shadow-2xl hover:shadow-rose-500/25 hover:scale-105 active:scale-95 text-gray-200'
                      : selected === idx
                      ? 'bg-rose-600/30 border-rose-500 scale-105 shadow-xl shadow-rose-500/30 text-white glow'
                      : 'bg-gray-700/20 text-gray-300 opacity-50 scale-95'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>

            {feedback && (
              <div className={`mt-4 p-3 rounded-xl text-sm sm:text-base font-medium smooth-transition ${
                feedback.includes('Correct')
                  ? 'bg-emerald-900/50 border-emerald-500/30 border text-emerald-300 animate-pulse'
                  : feedback.includes('Quest')
                  ? 'bg-pink-900/50 border-pink-500/30 border text-pink-300 glow'
                  : 'bg-rose-900/50 border-rose-500/30 border text-rose-300'
              } slide-in`}>
                {feedback}
              </div>
            )}
          </div>

          {feedback.includes('Quest') && (
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  const nextQuestIndex = (DUMMY_QUESTS.findIndex(q => q.id === currentQuest.id) + 1) % DUMMY_QUESTS.length;
                  const nextQuest = DUMMY_QUESTS[nextQuestIndex];
                  setCurrentQuest(nextQuest);
                  setCurrentTaskIndex(0);
                  setFeedback("");
                }}
                className="flex-1 py-3 px-6 rounded-xl font-semibold text-white bg-rose-600 shadow-lg hover:bg-rose-500 hover:shadow-2xl hover:shadow-rose-500/40 hover:scale-105 active:scale-95 smooth-transition text-sm sm:text-base"
              >
                Next Quest
              </button>
            </div>
          )}
        </section>

        <section className="bg-gray-800/50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-xl border border-rose-800/30 mb-6 sm:mb-8 smooth-transition">
          <h3 className="text-sm sm:text-base font-bold mb-3 text-rose-200 slide-in">Achievements</h3>
          <ul className="text-sm sm:text-base space-y-2 list-none pl-0">
            {totalXP >= 100 && <li className="flex items-center gap-2 text-pink-300 slide-in"><span className="text-lg">🌟</span> Knowledge Hero</li>}
            {totalXP >= 50 && <li className="flex items-center gap-2 text-emerald-300 slide-in"><span className="text-lg">💡</span> Fast Learner</li>}
            {totalXP >= 10 && <li className="flex items-center gap-2 text-blue-300 slide-in"><span className="text-lg">🏅</span> Beginner Explorer</li>}
            {totalXP < 10 && <li className="flex items-center gap-2 text-rose-300 slide-in"><span className="text-lg">✨</span> Start your first quest!</li>}
          </ul>
        </section>

        <footer className="flex justify-center">
          <button
            onClick={resetGame}
            className="px-6 py-3 text-sm sm:text-base border-2 border-rose-600 rounded-xl bg-transparent text-rose-300 hover:bg-rose-900/50 hover:scale-105 active:scale-95 smooth-transition shadow-lg hover:shadow-xl hover:shadow-rose-500/25"
          >
            Reset Progress
          </button>
        </footer>
      </main>
    </div>
  );
}